export const QUIZ_EMAIL_CAPTURE_EVENT = "quiz_email_captured"

export const QUIZ_SLUGS = [
  "is-my-boss-toxic",
  "am-i-a-toxic-boss",
  "calm-company-fit-score",
  "am-i-burned-out",
  "should-i-quit-my-job",
  "is-my-workplace-toxic",
] as const

const CAPTURE_TOKEN = /^[0-9a-f]{8}$/
const SENT_MARKER = "sent"
export const CAPTURE_DETAILS_WAIT_MS = 300

export interface QuizEmailCaptureDetails {
  quiz: string
  score?: string
  tier?: string
}

export type QuizEmailCaptureStatus =
  "sent" | "already" | "unavailable" | "missing"

type CaptureStorage = Pick<Storage, "getItem" | "setItem">

export function isQuizSlug(value: string) {
  return (QUIZ_SLUGS as readonly string[]).includes(value)
}

export function emailCaptureTokenFromUrl(url: string) {
  const query = url.includes("?")
    ? url.slice(url.indexOf("?") + 1).split("#")[0]
    : ""
  const token = new URLSearchParams(query).get("email_captured")
  return token && CAPTURE_TOKEN.test(token) ? token : null
}

export function quizSlugFromPath(pathname: string) {
  const parts = pathname.split("?")[0].split("/").filter(Boolean)
  const quizIndex = parts.lastIndexOf("quiz")
  const slug = quizIndex >= 0 ? parts[quizIndex + 1] : undefined
  return slug && isQuizSlug(slug) ? slug : null
}

let notedDetails: QuizEmailCaptureDetails | null = null

export function noteQuizEmailCaptureDetails(details: QuizEmailCaptureDetails) {
  notedDetails = details
}

export function notedQuizEmailCaptureDetails() {
  return notedDetails
}

export function resetQuizEmailCaptureDetails() {
  notedDetails = null
}

export function captureDetailsReady(
  quiz: string,
  noted: QuizEmailCaptureDetails | null,
  elapsedMs: number,
  waitMs = CAPTURE_DETAILS_WAIT_MS,
) {
  if (noted?.quiz === quiz) return true
  return elapsedMs >= waitMs
}

function storageKey(token: string) {
  return `quiz-email-captured:${token}`
}

export function recordQuizEmailCapture({
  token,
  quiz,
  score,
  tier,
  track,
  storage,
}: {
  token: string
  quiz: string
  score?: string
  tier?: string
  track: (name: string, params: Record<string, string>) => boolean
  storage?: CaptureStorage | null
}): QuizEmailCaptureStatus {
  if (!CAPTURE_TOKEN.test(token) || !isQuizSlug(quiz)) return "missing"

  const store =
    storage === undefined
      ? typeof sessionStorage === "undefined"
        ? null
        : sessionStorage
      : storage
  const key = storageKey(token)
  try {
    if (store?.getItem(key) === SENT_MARKER) return "already"
  } catch {
    // Private browsing can block storage. A later successful send still counts.
  }

  const params: Record<string, string> = { quiz }
  if (score !== undefined) params.score = score
  if (tier) params.tier = tier

  let sent = false
  try {
    sent = track(QUIZ_EMAIL_CAPTURE_EVENT, params)
  } catch {
    sent = false
  }
  if (!sent) return "unavailable"

  try {
    store?.setItem(key, SENT_MARKER)
  } catch {
    // The event already left the browser. Losing the marker can repeat it.
  }
  return "sent"
}

export function emailCapturedSearchWithoutToken(search: string) {
  const params = new URLSearchParams(search)
  if (!params.has("email_captured")) return null
  params.delete("email_captured")
  const next = params.toString()
  return next ? `?${next}` : ""
}
